/// <amd-module name="Artifacts/Feed/components/UnavailablePackageListTable" />
import * as Res from "Artifacts/Feed/Resources";
import { ProblemPackageItem } from "Artifacts/Feed/types/ProblemPackageItem";
import { IProtocolService } from "Artifacts/Root/protocols/services/ProtocolService";
import { IProtocol } from "Artifacts/Root/protocols/types";
import * as RootRes from "Artifacts/Root/Resources";
import { IFeed } from "Artifacts/Root/types";
import { ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import * as Utils_String from "VSS/Core/Util/String";
import { useComponentContext, WrappedComponent } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Card } from "VSSUI/Card";
import { Link } from "VSSUI/Link";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import {
  ColumnFill,
  ITableBreakpoint,
  ITableColumn,
  renderSimpleCell,
  SimpleTableCell,
  Table,
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { getSafeId } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

interface IUnavailablePackageListTableItem {
  protocol: IProtocol;
  name: string;
  version: string;
  source: string;
  errorStatus: string;
  failedDate: Date;
  description: string;
  linkHref: string;
}

const tableBreakpoints: ITableBreakpoint[] = [
  {
    breakpoint: ScreenBreakpoints.xsmall,
    columnWidths: [0, -100, -100, -0, -125, 0],
  },
  {
    breakpoint: ScreenBreakpoints.small,
    columnWidths: [50, -150, -100, -0, -100, -50],
  },
  {
    breakpoint: ScreenBreakpoints.large,
    columnWidths: [50, -150, -100, -75, -100, -150],
  },
];

const packageListTableColumns = [
  {
    id: "protocol",
    name: Res.PackageListTable_TableColumn_Protocol,
    readonly: true,
    renderCell: renderProtocol,
    width: new ObservableValue(50),
  },
  {
    id: "name",
    name: Res.PackageListTable_TableColumn_Name,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-100),
  },
  {
    id: "version",
    name: Res.ProblemPackageListTable_TableColumn_Version,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-50),
  },
  {
    id: "source",
    name: Res.PackageListTable_TableColumn_Source,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-50),
  },
  {
    id: "status",
    name: Res.ProblemPackageListTable_TableColumn_Status,
    renderCell: renderStatus,
    width: new ObservableValue(-100),
  },
  {
    id: "description",
    name: Res.PackageListTable_TableColumn_Description,
    readonly: true,
    renderCell: renderSimpleCell,
    width: new ObservableValue(-100),
  },
  ColumnFill,
];

export interface IPackageListTableProps {
  problemPackageData?: ProblemPackageItem[];
  feed?: IFeed;
  view?: ViewIdentifier;
}

const tableShimmer = new Array(30).fill(
  new ObservableValue<IUnavailablePackageListTableItem | undefined>(undefined),
);

const UnavailablePackageListTable: React.FC<IPackageListTableProps> = ({
  problemPackageData,
  feed,
}) => {
  const { pageContext } = useComponentContext();

  const tableItems = React.useMemo(() => {
    const protocolService: IProtocolService = pageContext.getService<IProtocolService>(
      "IProtocolService",
    );

    if (feed) {
      const items = problemPackageData
        ? problemPackageData.map<IUnavailablePackageListTableItem>((problemPackageDataItem) => ({
            protocol: protocolService.getProtocolById(problemPackageDataItem.protocolId),
            name: problemPackageDataItem.packageName,
            source: problemPackageDataItem.sourceName,
            description: problemPackageDataItem.description,
            version: problemPackageDataItem.packageVersion,
            errorStatus: Res.ProblemPackagesErrorStatus_FailedIngestion,
            failedDate: problemPackageDataItem.failedDate,
            linkHref: Utils_String.format(
              Res.ProblemPackagesResolveLink_Format,
              problemPackageDataItem.packageName,
              problemPackageDataItem.packageVersion,
              problemPackageDataItem.status,
            ),
          }))
        : tableShimmer;
      return new ArrayItemProvider<IUnavailablePackageListTableItem | undefined>(items);
    }
    return new ArrayItemProvider<IUnavailablePackageListTableItem | undefined>(tableShimmer);
  }, [problemPackageData, pageContext, feed]);

  const ariaMessageId = "problem-package-table-aria-description";
  return (
    <Card
      className="flex-grow bolt-table-card package-list-card"
      contentProps={{ contentPadding: false }}
    >
      <div className="visibility-hidden" id={getSafeId(ariaMessageId)}>
        {RootRes.ProblemPackageListSelection_ScreenreaderHelp}
      </div>
      <section
        aria-label={Res.ProblemPackageListTable_PackageList}
        aria-describedby={getSafeId(ariaMessageId)}
      >
        <Table
          tableBreakpoints={tableBreakpoints}
          columns={packageListTableColumns}
          itemProvider={tableItems}
        />
      </section>
    </Card>
  );
};

function renderStatus(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IUnavailablePackageListTableItem>,
  tableItem: IUnavailablePackageListTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <div className={"flex-column text-ellipsis"}>
        <div className={"flex-row"}>
          <Status
            {...Statuses.Failed}
            key="failed"
            size={StatusSize.s}
            className="flex-self-center failed-package-spaced"
          />
          <Link href={tableItem.linkHref}>{tableItem.errorStatus} </Link>
        </div>
        <Ago className={"fontSizeS"} date={tableItem.failedDate} />
      </div>
    </SimpleTableCell>
  );
}

function renderProtocol(
  rowIndex: number,
  columnIndex: number,
  tableColumn: ITableColumn<IUnavailablePackageListTableItem>,
  tableItem: IUnavailablePackageListTableItem,
): JSX.Element {
  return (
    <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex}>
      <Tooltip text={tableItem.protocol.name}>
        <div>
          <WrappedComponent
            wrappedType={tableItem.protocol.getLogoContributionId()}
            dependencies={[tableItem.protocol.getLogoContributionId()]}
            wrappedProps={{ className: "table-protocol-icon", alt: tableItem.protocol.name }}
          />
        </div>
      </Tooltip>
    </SimpleTableCell>
  );
}

export { UnavailablePackageListTable };
